import React, {
  useContext,
  useDeferredValue,
  useMemo,
  useState,
} from "react";

import InvoiceTemplate from "../components/InvoiceTemplate";
import { ProductContext } from "../context/ProductContext";

import logo from "../assets/Alogo.png";
import noImage from "../assets/no-image.png";
import jsPDF from "jspdf";

import autoTable from "jspdf-autotable";

import { CustomerContext } from "../context/CustomerContext";
import getThermalPrintTemplate from "../print/printTemplate";

import { db } from "../firebase";
import { collection, addDoc } from "firebase/firestore";
import { serverTimestamp } from "firebase/firestore";
import { getShopId } from "../utils/shop";

import websiteQR from "../assets/website-qr.png";

import {
  FaPlusCircle,
  FaShoppingCart,
  FaTag,
} from "react-icons/fa";

import "../styles/Billing.css";

import html2pdf from "html2pdf.js";

function Billing() {
  const [editingId, setEditingId] = useState(null);

  const {
    products,
    completeSale,
    saveSale,
    cart,
    addToCart,
    setCart,
    increaseQty,
    decreaseQty,
  } = useContext(ProductContext);
  
  const {
    customers,
    setCustomers,
    addCustomer
  } = useContext(CustomerContext);

  const [selectedCustomer, setSelectedCustomer] = useState(customers[0]);
  const [showBillPreview, setShowBillPreview] = useState(false);
  const [previewHTML, setPreviewHTML] = useState("");
  const [previewInvoice, setPreviewInvoice] = useState(null);

  const [showChargesModal, setShowChargesModal] = useState(false);
  const [deliveryCharge, setDeliveryCharge] = useState("");
  const [labourCharge, setLabourCharge] = useState("");
  const [packingCharge, setPackingCharge] = useState("");
  const [plantCharge, setPlantCharge] = useState("");
  const [cardCharge, setCardCharge] = useState("");
  const [unloadingCharge, setUnloadingCharge] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const deferredSearchTerm = useDeferredValue(searchTerm);
  const [cardSize, setCardSize] = useState(180);

  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState("amount");
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountInput, setDiscountInput] = useState("");
  const [receivedAmount, setReceivedAmount] = useState("");

  const generateInvoiceNo = () => {
    const lastNo = Number(localStorage.getItem("lastInvoiceNo") || 0) + 1;
    localStorage.setItem("lastInvoiceNo", lastNo);
    return `AAPos/${String(lastNo).padStart(4, "0")}`;
  };

  /* TOTALS */
  const subtotal = cart.reduce((sum, item) => {
    const price = Number(String(item.price).replace("₹", ""));
    return sum + (price * item.qty);
  }, 0);

  const saveInvoiceToCloud = async (finalInvoiceNo) => {
    try {
      const invoiceNo = finalInvoiceNo;
      const shopId = getShopId();

      await addDoc(
        collection(db, "shops", shopId, "invoices"),
        {
          invoiceNo,
          customerName: customerName || selectedCustomer.name,
          customerPhone: customerPhone || selectedCustomer.phone,
          paymentMethod,
          items: cart,
          subtotal,
          cgst: totalCGST,
          sgst: totalSGST,
          discount,
          deliveryCharge,
          labourCharge,
          packingCharge,
          plantCharge,
          cardCharge,
          unloadingCharge,
          grandTotal,
          paidAmount: Number(receivedAmount || 0),
          changeAmount: Number(changeAmount || 0),
          createdAt: serverTimestamp(),
        }
      );
      console.log("Invoice saved successfully");
    } catch (error) {
      console.error("Invoice save error:", error);
    }
  };

  const totalCGST = cart.reduce((sum, item) => {
    const price = Number(String(item.price).replace("₹", ""));
    const itemSubtotal = price * item.qty;
    const gstRate = Number(item.gstRate || 0);
    const gstAmount = itemSubtotal * (gstRate / 100);
    return sum + (gstAmount / 2);
  }, 0);

  const [heldBills, setHeldBills] = useState(
    JSON.parse(localStorage.getItem("heldBills")) || []
  );

  const totalSGST = cart.reduce((sum, item) => {
    const price = Number(String(item.price).replace("₹", ""));
    const itemSubtotal = price * item.qty;
    const gstRate = Number(item.gstRate || 0);
    const gstAmount = itemSubtotal * (gstRate / 100);
    return sum + (gstAmount / 2);
  }, 0);

  const grandTotal =
    subtotal +
    totalCGST +
    totalSGST -
    (Number(discount) || 0) +
    Number(deliveryCharge || 0) +
    Number(labourCharge || 0) +
    Number(packingCharge || 0) +
    Number(plantCharge || 0) +
    Number(cardCharge || 0) +
    Number(unloadingCharge || 0);

  const changeAmount = Math.max(0, Number(receivedAmount || 0) - grandTotal).toFixed(0);

  const generateInvoice = () => {
    if (customerName && customerPhone) {
      const exists = customers.some((c) => c.phone === customerPhone);
      if (!exists) {
        addCustomer({
          id: Date.now(),
          name: customerName,
          phone: customerPhone,
        });
      }
    }

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: [200, 80],
    });

    doc.setFontSize(22);
    doc.text("GREEN LEAF GARDEN CENTER", 14, 20);
    doc.setFontSize(11);
    doc.text("GSTIN: 27AAAAA0000A1Z5", 14, 28);
    doc.text("Phone: +91 9876543210", 14, 34);
    doc.text(`Invoice Date: ${new Date().toLocaleDateString()}`, 140, 28);

    const tableData = cart.map((item) => [
      item.name,
      item.qty,
      item.price,
      `₹${Number(item.price.replace("₹", "")) * item.qty}`,
    ]);

    autoTable(doc, {
      startY: 45,
      head: [["Product", "Qty", "Price", "Total"]],
      body: tableData,
    });

    const finalY = (doc.lastAutoTable?.finalY || 60) + 12;
    doc.text(`Subtotal: ₹${subtotal.toFixed(2)}`, 140, finalY);
    doc.text(`CGST: ₹${totalCGST.toFixed(2)}`, 140, finalY + 24);
    doc.text(`SGST: ₹${totalSGST.toFixed(2)}`, 140, finalY + 24);
    doc.setFontSize(16);
    doc.text(`Grand Total: ₹${grandTotal.toFixed(2)}`, 140, finalY + 28);

    doc.save("invoice.pdf");

    saveSale({
      name: customerName || selectedCustomer.name,
      phone: customerPhone || selectedCustomer.phone,
      paymentMethod: paymentMethod,
    });

    setCart([]);
    setDiscount(0);
  };

  const categories = useMemo(() => [
    "All Products",
    ...new Set(products.map((item) => item.category).filter(Boolean)),
  ], [products]);

  const [activeCategory, setActiveCategory] = useState("All Products");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [filteredCustomers, setFilteredCustomers] = useState([]);

  // ==============================================================
  // NEW FEATURE: Sorts Cart Items to the Top on "All Products"
  // ==============================================================
  const filteredProducts = useMemo(() => {
    // 1. Standard Filter
    const filtered = products.filter((item) => {
      const matchesCategory =
        activeCategory === "All Products" ||
        item.category === activeCategory;

      const term = deferredSearchTerm ? deferredSearchTerm.toLowerCase() : "";
      
      const matchesSearch =
        !term ||
        (item.name && item.name.toLowerCase().includes(term)) ||
        (item.sku && item.sku.toLowerCase().includes(term));

      return matchesCategory && matchesSearch;
    });

    // 2. Automatic Sorting Logic
    if (activeCategory === "All Products") {
      // Create a fast lookup list of items currently in the cart
      const cartItemIds = new Set(cart.map((cartItem) => cartItem.id));
      
      // Sort the filtered array so cart items appear first
      filtered.sort((a, b) => {
        const aInCart = cartItemIds.has(a.id);
        const bInCart = cartItemIds.has(b.id);
        
        if (aInCart && !bInCart) return -1; // Move item 'a' to the top
        if (!aInCart && bInCart) return 1;  // Move item 'b' to the top
        return 0; // Keep their original order otherwise
      });
    }

    return filtered;
  }, [products, activeCategory, deferredSearchTerm, cart]); // Cart added as dependency so it updates instantly
  // ==============================================================

  const downloadBill = () => {
    const element = document.getElementById("bill-print");
    const options = {
      margin: 0.5,
      filename: `Invoice-${Date.now()}.pdf`,
      image: { type: "jpeg", quality: 1 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
    };
    html2pdf().set(options).from(element).save();
  };

  const printBill = async () => {
    const ipcRenderer = window.require ? window.require("electron").ipcRenderer : null;
    const receiptHTML = getThermalPrintTemplate({
      cart,
      subtotal,
      totalCGST,
      totalSGST,
      grandTotal,
      discount,
      paymentMethod,
      invoiceNo: "INV-" + Date.now(),
      customerName: customerName || selectedCustomer.name,
      customerPhone: customerPhone || selectedCustomer.phone,
    });

    if (ipcRenderer) {
      await ipcRenderer.invoke("print-bill", receiptHTML);
    }
  };

  const saveInvoice = (finalInvoiceNo) => {
    const invoices = JSON.parse(localStorage.getItem("invoices")) || [];
    const amount = grandTotal;
    const gstAmount = totalCGST + totalSGST;

    const newInvoice = {
      invoiceNo: finalInvoiceNo,
      items: cart,
      total: amount,
      gst: gstAmount,
      cgst: totalCGST,
      sgst: totalSGST,
      customer: customerName || selectedCustomer.name,
      customerPhone: customerPhone || selectedCustomer.phone,
      paymentMethod,
      date: new Date().toISOString()
    };

    invoices.push(newInvoice);
    localStorage.setItem("invoices", JSON.stringify(invoices));
  };

  const applyDiscount = () => {
    const input = discountInput.trim();
    if (!input) return;

    let discountValue = 0;
    if (input.includes("%")) {
      const percent = parseFloat(input.replace("%", ""));
      if (!isNaN(percent)) {
        discountValue = (subtotal * percent) / 100;
      }
    } else {
      const amount = parseFloat(input);
      if (!isNaN(amount)) {
        discountValue = amount;
      }
    }

    setDiscount(discountValue);
    setShowDiscountModal(false);
    setDiscountInput("");
  };

  const printInvoice = () => {
    const receipt = document.getElementById("printable-receipt");
    if (!receipt) {
      alert("Receipt not found");
      return;
    }

    const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
      .map((el) => el.outerHTML)
      .join("");

    const printWindow = window.open("", "", "width=400,height=800");
    printWindow.document.write(`
      <html>
        <head>
          ${styles}
          <style>
            body { margin: 0 !important; padding: 0 !important; overflow-x: hidden !important; }
            #print-area { width: 80mm !important; max-width: 80mm !important; overflow-x: hidden !important; overflow-y: hidden !important; margin: 0 auto !important; padding: 0 !important; }
            .invoice-modal { position:static !important; background:white !important; }
            .invoice-content { width: 78mm !important; max-width: 78mm !important; overflow-x: hidden !important; box-sizing: border-box !important; }
            .bill-table-header, .bill-item-row { width: 100% !important; overflow: hidden !important; }
            .bill-buttons { display:none !important; }
            @page { size:80mm auto; margin:0; }
          </style>
        </head>
        <body>
          <div id="print-area">${receipt.outerHTML}</div>
        </body>
      </html>
    `);

    printWindow.document.close();
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 500);
  };

  const handlePrintAndSave = async () => {
    try {
      printInvoice();
      await saveInvoiceToCloud(previewInvoice.invoiceNo);
      saveInvoice(previewInvoice.invoiceNo);
      completeSale();
      setShowBillPreview(false);

      setDiscount(0);
      setCustomerName("");
      setCustomerPhone("");
      setPaymentMethod("Cash");
      setDeliveryCharge(0);
      setLabourCharge(0);
      setPackingCharge(0);
      setPlantCharge(0);
      setCardCharge(0);
      setUnloadingCharge(0);
      setReceivedAmount("");
    } catch (error) {
      console.error(error);
      alert("Failed to save invoice");
    }
  };

  const billItems = previewInvoice
    ? [
        ...previewInvoice.items,
        ...(deliveryCharge > 0 ? [{ name: "Delivery Charges", qty: 1, price: deliveryCharge }] : []),
        ...(labourCharge > 0 ? [{ name: "Labour Charges", qty: 1, price: labourCharge }] : []),
        ...(packingCharge > 0 ? [{ name: "Packing Charges", qty: 1, price: packingCharge }] : []),
        ...(plantCharge > 0 ? [{ name: "Plants Charges", qty: 1, price: plantCharge }] : []),
        ...(cardCharge > 0 ? [{ name: "Card Charges", qty: 1, price: cardCharge }] : []),
        ...(unloadingCharge > 0 ? [{ name: "Unloading Charges", qty: 1, price: unloadingCharge }] : [])
      ]
    : [];

  const totalSavings = (previewInvoice?.items || []).reduce((sum, item) => {
    const mrp = Number(String(item.mrpPrice || 0).replace(/[^\d.]/g, ""));
    const rate = Number(String(item.price || 0).replace(/[^\d.]/g, ""));
    const qty = Number(item.qty || 1);
    return sum + Math.max(0, (mrp - rate) * qty);
  }, 0) + Number(previewInvoice?.discount || 0);

  const handleSaveOnly = async () => {
    try {
      await saveInvoiceToCloud(previewInvoice.invoiceNo);
      saveInvoice(previewInvoice.invoiceNo);
      completeSale();
      setShowBillPreview(false);

      setDiscount(0);
      setCustomerName("");
      setCustomerPhone("");
      setPaymentMethod("Cash");
      setDeliveryCharge(0);
      setLabourCharge(0);
      setPackingCharge(0);
      setPlantCharge(0);
      setCardCharge(0);
      setUnloadingCharge(0);
      setReceivedAmount("");
      alert("Bill Saved Successfully");
    } catch (error) {
      console.error(error);
      alert("Failed to save bill");
    }
  };

  return (
    <div className="billing-page">
      {/* LEFT */}
      <div className="billing-left">
        <div className="product-search">
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="category-header">
          <div className="category-tabs">
            {categories.map((cat, index) => (
              <button
                key={index}
                className={activeCategory === cat ? "active-tab" : ""}
                onClick={() => setActiveCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="product-size-control">
            <span>🔍 Size</span>
            <input
              type="range"
              min="100"
              max="300"
              value={cardSize}
              onChange={(e) => setCardSize(Number(e.target.value))}
            />
          </div>
        </div>

        {/* PRODUCTS */}
        <div
          className="products-grid"
          style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${cardSize}px, 1fr))` }}
        >
          {filteredProducts.map((item, index) => {
            const isInCart = cart.some((cartItem) => cartItem.id === item.id);

            return (
              <div
                key={index}
                className={`product-card ${isInCart ? "in-cart" : ""}`}
                onClick={() => addToCart(item)}
                style={{
                  width: `${cardSize}px`,
                  minHeight: `${cardSize * 1.8}px`,
                  cursor: "pointer",
                }}
              >
                <div
                  className="product-image-wrapper"
                  style={{ height: `${cardSize * 0.9}px` }}
                >
                  {isInCart && (
                    <div className="in-cart-badge">✓ IN CART</div>
                  )}

                  <span
                    className={
                      item.units > 0
                        ? "stock-tag in-stock"
                        : "stock-tag out-stock"
                    }
                  >
                    {item.units > 0 ? "IN STOCK" : "OUT OF STOCK"}
                  </span>

                  <img
                    src={item.images?.[0] || item.image || noImage}
                    alt=""
                  />
                </div>

                <div className="product-details">
                  <h4 style={{ fontSize: `${cardSize / 10}px` }}>
                    {item.name}
                  </h4>
                  <span>{item.sku}</span>
                  <div className="product-bottom">
                    <strong style={{ fontSize: `${cardSize / 8}px` }}>
                      ₹{Number(String(item.price).replace("₹", "")) || 0}
                    </strong>
                    <FaPlusCircle
                      className={isInCart ? "added-icon" : ""}
                      onClick={(e) => {
                        e.stopPropagation();
                        addToCart(item);
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* RIGHT */}
      <div className="billing-right">
        {/* CUSTOMER */}
        <div className="customer-box">
          <label>CUSTOMER SELECTION</label>
          <select
            className="customer-dropdown"
            value={selectedCustomer?.id || ""}
            onChange={(e) => {
              const customer = customers.find(
                (c) => c.id === Number(e.target.value)
              );
              setSelectedCustomer(customer);
            }}
          >
            {customers.map((customer) => (
              <option key={customer.id} value={customer.id}>
                {customer.name}
              </option>
            ))}
          </select>
        </div>

        <div className="walkin-customer-box">
          <input
            type="text"
            placeholder="Customer Name"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
          />

          <div className="customer-search-wrapper">
            <input
              type="text"
              placeholder="Phone Number"
              value={customerPhone}
              onChange={(e) => {
                const phone = e.target.value;
                setCustomerPhone(phone);
                if (phone.trim() === "") {
                  setFilteredCustomers([]);
                  return;
                }
                const matches = customers.filter(
                  (customer) =>
                    customer.phone && customer.phone.includes(phone)
                );
                setFilteredCustomers(matches);
              }}
            />

            {filteredCustomers.length > 0 && (
              <div className="customer-dropdown">
                {filteredCustomers.map((customer, index) => (
                  <div
                    key={index}
                    className="customer-dropdown-item"
                    onClick={() => {
                      setCustomerName(customer.name);
                      setCustomerPhone(customer.phone);
                      setFilteredCustomers([]);
                    }}
                  >
                    <strong>{customer.name}</strong>
                    <span>{customer.phone}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* CART */}
        <div id="thermal-bill" className="cart-section">
          <div
            id="bill-print"
            style={{
              position: "absolute",
              left: "-9999px",
              top: "0",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <h2>Anushika Agri Products</h2>
              <p>Thank You Visit Again</p>
            </div>
            <hr />
            {cart.map((item, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "5px",
                }}
              >
                <span>
                  {item.name} x {item.qty}
                </span>
                <span>
                  ₹{Number(String(item.price).replace("₹", "")) * item.qty}
                </span>
              </div>
            ))}
            <hr />
            <p>Subtotal : ₹{subtotal.toFixed(2)}</p>
            <p>CGST : ₹{totalCGST.toFixed(2)}</p>
            <p>SGST : ₹{totalSGST.toFixed(2)}</p>
            <h3>Total : ₹{grandTotal.toFixed(2)}</h3>
          </div>

          <div className="cart-header">
            <h3>Active Cart</h3>
            <span>{cart.length} Items</span>
          </div>

          <div className="cart-table-header">
            <span>Item Name</span>
            <span>Qty</span>
            <span>MRP</span>
            <span>Rate</span>
            <span>Amount</span>
            <span></span>
          </div>

          {cart.map((item) => (
            <div className="cart-item" key={item.id}>
              <span className="cart-name" title={item.name}>
                {item.name}
              </span>

              <div className="qty-control">
                <button onClick={() => decreaseQty(item.id)}>-</button>
                <span>{item.qty}</span>
                <button onClick={() => increaseQty(item.id)}>+</button>
              </div>

              {/* MRP */}
              <span className="cart-mrp">
                ₹{Number(String(item.mrpPrice || item.price).replace(/[^\d.]/g, ""))}
              </span>

              {/* Editable Rate */}
              {editingId === item.id ? (
                <span
                  contentEditable
                  suppressContentEditableWarning
                  className="cart-price-edit"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      const newPrice = e.currentTarget.innerText.replace("₹", "").trim();
                      setCart(
                        cart.map((cartItem) =>
                          cartItem.id === item.id
                            ? { ...cartItem, price: `₹${newPrice}` }
                            : cartItem
                        )
                      );
                      setEditingId(null);
                    }
                  }}
                  onBlur={(e) => {
                    const newPrice = e.target.innerText.replace("₹", "").trim();
                    setCart(
                      cart.map((cartItem) =>
                        cartItem.id === item.id
                          ? { ...cartItem, price: `₹${newPrice}` }
                          : cartItem
                      )
                    );
                    setEditingId(null);
                  }}
                >
                  ₹{Number(String(item.price).replace(/[^\d.]/g, ""))}
                </span>
              ) : (
                <span
                  className="cart-price"
                  onClick={() => setEditingId(item.id)}
                >
                  ₹{Number(String(item.price).replace(/[^\d.]/g, ""))}
                </span>
              )}

              {/* Amount */}
              <span className="cart-amount">
                ₹{Number(String(item.price).replace(/[^\d.]/g, "")) * item.qty}
              </span>

              <button
                className="remove-cart-item"
                onClick={() =>
                  setCart(cart.filter((cartItem) => cartItem.id !== item.id))
                }
              >
                ✕
              </button>
            </div>
          ))}
        </div>

        {/* SUMMARY + PAYMENT AREA */}
        <div className="billing-bottom">
          <div className="billing-summary">
            <div>
              <span>Subtotal</span>
              <strong>₹{subtotal.toFixed(2)}</strong>
            </div>
            <div>
              <span>CGST</span>
              <strong>₹{totalCGST.toFixed(2)}</strong>
            </div>
            <div>
              <span>SGST</span>
              <strong>₹{totalSGST.toFixed(2)}</strong>
            </div>
            <div className="summary-row">
              <span>Discount</span>
              <span>
                - ₹{discount.toFixed(2)}
                {discount > 0 && (
                  <button
                    onClick={() => setDiscount(0)}
                    style={{
                      marginLeft: "8px",
                      border: "none",
                      background: "transparent",
                      color: "red",
                      cursor: "pointer",
                    }}
                  >
                    ✕
                  </button>
                )}
              </span>
            </div>

            {deliveryCharge > 0 && (
              <div>
                <span>Delivery</span>
                <strong>₹{deliveryCharge}</strong>
              </div>
            )}
            {labourCharge > 0 && (
              <div>
                <span>Labour</span>
                <strong>₹{labourCharge}</strong>
              </div>
            )}
            {packingCharge > 0 && (
              <div>
                <span>Packing</span>
                <strong>₹{packingCharge}</strong>
              </div>
            )}
            {plantCharge > 0 && (
              <div>
                <span>Plants</span>
                <strong>₹{plantCharge}</strong>
              </div>
            )}
            {cardCharge > 0 && (
              <div>
                <span>Card</span>
                <strong>₹{cardCharge}</strong>
              </div>
            )}
            {unloadingCharge > 0 && (
              <div>
                <span>Unloading</span>
                <strong>₹{unloadingCharge}</strong>
              </div>
            )}

            <div className="final-total">
              <span>Total </span>
              <strong>₹{grandTotal.toFixed(2)}</strong>
            </div>
          </div>

          <div className="payment-side">
            <div className="payment-method">
              <label>Payment Method</label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                <option>Cash</option>
                <option>Card</option>
                <option>UPI</option>
              </select>
            </div>

            <div className="billing-actions">
              <button
                className="hold-btn"
                onClick={() => {
                  if (cart.length === 0) {
                    alert("Cart is empty");
                    return;
                  }
                  if (heldBills.length >= 5) {
                    alert("Only 5 hold bills allowed");
                    return;
                  }
                  const newHoldBill = {
                    id: Date.now(),
                    items: cart,
                    customerName,
                    customerPhone,
                    total: grandTotal,
                    paymentMethod,
                    deliveryCharge,
                    labourCharge,
                    packingCharge,
                    plantCharge,
                    cardCharge,
                    unloadingCharge,
                    discount,
                    date: new Date().toLocaleString(),
                  };
                  const updatedBills = [...heldBills, newHoldBill];
                  setHeldBills(updatedBills);
                  localStorage.setItem(
                    "heldBills",
                    JSON.stringify(updatedBills)
                  );
                  setCart([]);
                  setDiscount(0);
                  setCustomerName("");
                  setCustomerPhone("");
                  setPaymentMethod("Cash");
                  setDeliveryCharge(0);
                  setLabourCharge(0);
                  setPackingCharge(0);
                  setPlantCharge(0);
                  setCardCharge(0);
                  setUnloadingCharge(0);
                  setReceivedAmount("");
                  alert("Bill moved to Hold Cart");
                }}
              >
                HOLD
              </button>
              <button
                className="discount-btn"
                onClick={() => setShowDiscountModal(true)}
              >
                DISCOUNT
              </button>
              <button
                className="charges-btn"
                onClick={() => setShowChargesModal(true)}
              >
                EXTRA
              </button>
            </div>

            <div className="payment-info">
              <div className="payment-row">
                <label>Receive Amount</label>
                <input
                  type="number"
                  value={receivedAmount}
                  onChange={(e) => setReceivedAmount(e.target.value)}
                />
              </div>
              <div className="payment-row">
                <label>Change Amount</label>
                <input type="text" value={changeAmount} readOnly />
              </div>
            </div>
          </div>
        </div>

        {/* HELD BILLS */}
        <div className="hold-bills-list">
          {heldBills.map((bill) => (
            <div
              key={bill.id}
              className="hold-bill-card"
              onClick={() => {
                setCart(bill.items);
                setCustomerName(bill.customerName || "");
                setCustomerPhone(bill.customerPhone || "");
                setPaymentMethod(bill.paymentMethod || "Cash");
                setDeliveryCharge(bill.deliveryCharge || 0);
                setLabourCharge(bill.labourCharge || 0);
                setPackingCharge(bill.packingCharge || 0);
                setPlantCharge(bill.plantCharge || 0);
                setCardCharge(bill.cardCharge || 0);
                setUnloadingCharge(bill.unloadingCharge || 0);
                setDiscount(bill.discount || 0);

                const updatedBills = heldBills.filter((b) => b.id !== bill.id);
                setHeldBills(updatedBills);
                localStorage.setItem(
                  "heldBills",
                  JSON.stringify(updatedBills)
                );
              }}
            >
              <button
                className="remove-hold-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  const updatedBills = heldBills.filter((b) => b.id !== bill.id);
                  setHeldBills(updatedBills);
                  localStorage.setItem(
                    "heldBills",
                    JSON.stringify(updatedBills)
                  );
                }}
              >
                ×
              </button>
              <div className="hold-bill-top">
                <strong>₹{Number(bill.total || 0).toFixed(0)}</strong>
              </div>
              <div className="hold-bill-bottom">
                <span>{bill.items.length} Items</span>
                <span>{bill.paymentMethod}</span>
              </div>
            </div>
          ))}
        </div>

        {/* PAY */}
        <button
          className="complete-btn"
          onClick={async () => {
            try {
              const phone = customerPhone || selectedCustomer?.phone;
              const name = customerName || selectedCustomer?.name;
              const exists = customers.some((c) => c.phone === phone);

              if (name && phone && phone !== "0000000000" && !exists) {
                await addCustomer({
                  name,
                  phone,
                });
              }

              const invoiceNo = generateInvoiceNo();
              setPreviewInvoice({
                invoiceNo,
                customerName,
                customerPhone,
                paymentMethod,
                items: cart,
                subtotal,
                discount,
                cgst: totalCGST,
                sgst: totalSGST,
                grandTotal,
                paidAmount: receivedAmount,
                changeAmount: changeAmount,
                createdAt: {
                  seconds: Math.floor(Date.now() / 1000),
                },
              });
              setShowBillPreview(true);
            } catch (error) {
              console.error(error);
              alert("Failed to complete sale");
            }
          }}
        >
          <FaShoppingCart /> COMPLETE SALE & PRINT BILL
        </button>
      </div>

      {showDiscountModal && (
        <div className="discount-modal-overlay">
          <div className="discount-modal">
            <h3>Apply Discount</h3>
            <input
              type="text"
              placeholder="100 or 10%"
              value={discountInput}
              onChange={(e) => setDiscountInput(e.target.value)}
            />
            <div className="discount-actions">
              <button onClick={() => setShowDiscountModal(false)}>
                Cancel
              </button>
              <button onClick={applyDiscount}>Apply</button>
            </div>
          </div>
        </div>
      )}

      {showChargesModal && (
        <div className="charges-overlay">
          <div className="charges-modal">
            <div className="charges-header">
              <h2>Additional Charges</h2>
              <button
                className="close-btn"
                onClick={() => setShowChargesModal(false)}
              >
                ✕
              </button>
            </div>
            <p className="charges-subtitle">
              Add extra charges that will be included in the total amount.
            </p>

            <div className="charge-row">
              <div className="charge-icon delivery">🚚</div>
              <div className="charge-input-area">
                <label>Delivery Charges</label>
                <input
                  type="number"
                  placeholder="0"
                  value={deliveryCharge}
                  onChange={(e) => setDeliveryCharge(e.target.value)}
                />
              </div>
            </div>

            <div className="charge-row">
              <div className="charge-icon labour">👷</div>
              <div className="charge-input-area">
                <label>Labour Charges</label>
                <input
                  type="number"
                  placeholder="0"
                  value={labourCharge}
                  onChange={(e) => setLabourCharge(e.target.value)}
                />
              </div>
            </div>

            <div className="charge-row">
              <div className="charge-icon packing">📦</div>
              <div className="charge-input-area">
                <label>Packing Charges</label>
                <input
                  type="number"
                  placeholder="0"
                  value={packingCharge}
                  onChange={(e) => setPackingCharge(e.target.value)}
                />
              </div>
            </div>

            <div className="charge-row">
              <div className="charge-icon">🪴</div>
              <div className="charge-input-area">
                <label>Plants Charges</label>
                <input
                  type="number"
                  placeholder="0"
                  value={plantCharge}
                  onChange={(e) => setPlantCharge(e.target.value)}
                />
              </div>
            </div>

            <div className="charge-row">
              <div className="charge-icon">💳</div>
              <div className="charge-input-area">
                <label>Card Charges</label>
                <input
                  type="number"
                  placeholder="0"
                  value={cardCharge}
                  onChange={(e) => setCardCharge(e.target.value)}
                />
              </div>
            </div>

            <div className="charge-row">
              <div className="charge-icon">📥</div>
              <div className="charge-input-area">
                <label>Unloading Charges</label>
                <input
                  type="number"
                  value={unloadingCharge}
                  onChange={(e) => setUnloadingCharge(e.target.value)}
                  placeholder="0"
                />
              </div>
            </div>

            <div className="extra-total">
              <span>Total Extra Charges</span>
              <strong>
                ₹
                {(
                  Number(deliveryCharge || 0) +
                  Number(labourCharge || 0) +
                  Number(packingCharge || 0) +
                  Number(plantCharge || 0) +
                  Number(cardCharge || 0) +
                  Number(unloadingCharge || 0)
                ).toFixed(2)}
              </strong>
            </div>

            <div className="charges-footer">
              <button
                className="cancel-charge-btn"
                onClick={() => setShowChargesModal(false)}
              >
                CANCEL
              </button>
              <button
                className="apply-charge-btn"
                onClick={() => setShowChargesModal(false)}
              >
                APPLY CHARGES
              </button>
            </div>
          </div>
        </div>
      )}

      {showBillPreview && previewInvoice && (
        <div className="invoice-modal">
          <InvoiceTemplate
            invoice={previewInvoice}
            billItems={billItems}
            subtotal={subtotal}
            discount={discount}
            totalCGST={totalCGST}
            totalSGST={totalSGST}
            grandTotal={grandTotal}
            totalSavings={totalSavings}
            onPrint={handlePrintAndSave}
            onSave={handleSaveOnly}
            onClose={() => setShowBillPreview(false)}
            showSave={true}
          />
        </div>
      )}
    </div>
  );
}

export default Billing;